import React, { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import DataExplorer from '../data-explorer/DataExplorer'
import StageSection from './StageSection'
import UploadFileStep, { UploadSelection } from './UploadFileStep'
import StudyContextEditor from './StudyContextEditor'
import DisabledReason from '../system/DisabledReason'
import { GenNode } from '../../types/carbon/models/GenNode'
import type { CarbonInterviewRawDataRow, ThemeAIDataSource, ThemeAIUploadInspection } from '../../types/gotypes'
import { THEMEAI_FILE_UPLOAD } from '../../utils/themeAIFeatures'

interface DataStageProps {
  customer: string
  job: string
  source?: ThemeAIDataSource
  poolSize: number
  previewRows: CarbonInterviewRawDataRow[]
  previewLoading: boolean
  replacing: boolean
  canContinue: boolean
  studyContext: string
  studyContextDraft?: string
  savingStudyContext: boolean
  onStudyContextDraftChange: (draft: string | undefined) => void
  onSaveStudyContext: () => Promise<void>
  onInspectUpload: (file: File) => Promise<ThemeAIUploadInspection>
  onReplaceWithFile: (selection: UploadSelection) => Promise<void>
  onReplaceWithVariable: (node: GenNode) => Promise<void>
  /** Re-runs the task's data source; offered when a job-variable task has none yet. */
  onFetchResponses: () => Promise<void>
  fetchingResponses: boolean
  /** The organisation and job context from Admin → Context. */
  jobContext?: string
  onOpenJobContext: () => void
  onContinue: () => void
  /** Why this person cannot change the task (its data or its context), or undefined. */
  writeBlockedReason?: string
}

const rowText = (row: CarbonInterviewRawDataRow) => row.data.map((column) => column.data).filter(Boolean).join(' · ')

// The Data step reads straight down, all left-aligned (#1492 item 8, Matt's
// #861 comment and the 23 Sep call): the task's context first, because the AI
// reads it before anything else; then the way on; then what is being coded and
// a handful of the responses, so the user can see it is the right text (not
// IDs, not codes) before building a frame against it. Matt settled the heading
// on the call: "maybe instead of task context, we'll say about this task".
const DataStage: React.FC<DataStageProps> = ({
  customer,
  job,
  source,
  poolSize,
  previewRows,
  previewLoading,
  replacing,
  canContinue,
  studyContext,
  studyContextDraft,
  savingStudyContext,
  onStudyContextDraftChange,
  onSaveStudyContext,
  onInspectUpload,
  onReplaceWithFile,
  onReplaceWithVariable,
  onFetchResponses,
  fetchingResponses,
  jobContext,
  onOpenJobContext,
  onContinue,
  writeBlockedReason,
}) => {
  const [replaceOpen, setReplaceOpen] = useState(false)
  const [tab, setTab] = useState<'upload' | 'job_variable'>(
    !THEMEAI_FILE_UPLOAD || source?.type === 'job_variable' ? 'job_variable' : 'upload',
  )
  const [upload, setUpload] = useState<UploadSelection>()
  const [node, setNode] = useState<GenNode>()

  const upl = source?.upload
  const title = source?.type === 'upload' ? upl?.file_name || 'Uploaded file' : source?.job_variable?.variable_name || 'Job variable'
  const detail =
    source?.type === 'upload'
      ? [`${poolSize.toLocaleString()} responses`, upl?.text_column && `from the “${upl.text_column}” column`, upl?.id_column && `IDs from “${upl.id_column}”`]
      : [`${poolSize.toLocaleString()} responses`, source?.job_variable?.question]

  const closeReplace = () => {
    if (replacing) return
    setReplaceOpen(false)
    setUpload(undefined)
    setNode(undefined)
  }

  const handleReplace = async () => {
    try {
      if (tab === 'upload' && upload) await onReplaceWithFile(upload)
      else if (tab === 'job_variable' && node) await onReplaceWithVariable(node)
      else return
      setReplaceOpen(false)
      setUpload(undefined)
      setNode(undefined)
    } catch {
      // The hook reports the error; the dialog keeps the choice for a retry.
    }
  }

  return (
    <StageSection
      title="About this task"
      description="Do you want to add some context about this task? The AI will use it to inform the process. For example, who was asked, why, and what the output is for."
    >
      <StudyContextEditor
        value={studyContext}
        draft={studyContextDraft}
        saving={savingStudyContext}
        onDraftChange={onStudyContextDraftChange}
        onSave={onSaveStudyContext}
        jobContext={jobContext}
        onOpenJobContext={onOpenJobContext}
        blockedReason={writeBlockedReason}
      />

      {/* Left-aligned under the context, not top-right. Matt, 23 Sep: "the first
          action point is add the context, click save, continue to code frame",
          with replacing the data as the quieter option after it. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="contained" color="info" onClick={onContinue} disabled={!canContinue}>
          Continue to code frame
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>or</Typography>
        <DisabledReason reason={writeBlockedReason}>
          <Button color="info" onClick={() => setReplaceOpen(true)} disabled={replacing} sx={{ ml: -0.5 }}>
            go back to replace data
          </Button>
        </DisabledReason>
      </Box>

      {/* Matt, 23 Sep: "Underneath those buttons, we have the Q9, the number of
          responses, survey, and then a few of the responses below." */}
      <Box sx={{ maxWidth: 880 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Data</Typography>
        <Typography variant="body2">{title}</Typography>
        <Typography variant="body2" color="text.secondary">{detail.filter(Boolean).join(' · ')}</Typography>
      </Box>

      <Box sx={{ maxWidth: 880 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>A few of the responses</Typography>
        {previewLoading ? (
          <Box>{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} height={36} />)}</Box>
        ) : previewRows.length === 0 && source?.type === 'job_variable' && poolSize === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              The responses from {source.job_variable?.variable_name || 'this variable'} have not been fetched yet.
            </Typography>
            <DisabledReason reason={writeBlockedReason}>
              <Button color="info" variant="outlined" onClick={() => onFetchResponses().catch(() => {})} disabled={fetchingResponses}>
                {fetchingResponses ? 'Fetching…' : 'Fetch the responses'}
              </Button>
            </DisabledReason>
          </Box>
        ) : previewRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No responses to show. Replace the data with a {THEMEAI_FILE_UPLOAD ? 'file or job variable' : 'job variable'} that has text in it.
          </Typography>
        ) : (
          <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none' }}>
            {previewRows.map((row) => (
              <Box
                component="li"
                key={row.interview_id}
                sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}
              >
                <Typography variant="body2" sx={{ maxWidth: '75ch', lineHeight: 1.6 }}>{rowText(row)}</Typography>
                <Typography variant="caption" color="text.disabled">{row.interview_id}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Dialog open={replaceOpen} onClose={closeReplace} maxWidth="md" fullWidth>
        <DialogTitle>Replace the data</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            The task codes the new responses from here on. Earlier runs stay in the run history for reference.
          </Typography>
          {THEMEAI_FILE_UPLOAD && (
            <Tabs value={tab} onChange={(_, value) => setTab(value)} textColor="inherit" indicatorColor="primary">
              <Tab value="upload" label="Upload a file" />
              <Tab value="job_variable" label="Use a job variable" />
            </Tabs>
          )}
          {tab === 'upload' ? (
            <UploadFileStep onInspect={onInspectUpload} onChange={setUpload} disabled={replacing} />
          ) : (
            <Box sx={{ height: 380, border: '1px solid', borderColor: 'divider' }}>
              <DataExplorer customer={customer} job={job} onVariableChange={setNode} onDataChange={() => { /* preview only */ }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeReplace} color="inherit" disabled={replacing}>Cancel</Button>
          <Button
            variant="contained"
            color="info"
            disabled={replacing || (tab === 'upload' ? !upload?.columns.textColumn : !node)}
            onClick={handleReplace}
          >
            {replacing ? 'Replacing…' : 'Replace data'}
          </Button>
        </DialogActions>
      </Dialog>
    </StageSection>
  )
}

export default DataStage
